import moment from "moment";
import { Drivability, TrailerType } from "@/utils/enum";
import request from "@/utils/request";
import { buildDateCriteria, cleanObjectFromUndefined } from "@/utils/utils";

export async function getAllShipments({
  page = 1,
  pageSize = 10,
  filter = "",

  dealerId,
  pickUpAddress,
  dropOffAddress,
  status,
  distance,
  dealerName,
  dealerPrice,
  pickupETA,
  dropOffETA,
  postedAt,
  pickedUpAt,
  vehicleCount,
  creationTime,
  creatorId,

  sorting,
}) {
  const pageNumber = page - 1;

  let Sorting;

  const customSorting = {
    dealerName: "Dealer.Name",
    pickupDropoffLocation: "PickupAddress.SimplifyAddress",
    pickupEta: "load.pickupETA",
    dropoffEta: "load.dropoffETA",
  };

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");
    Sorting = `${customSorting[sortBy] || sortBy} ${sortOrder}`;
  }

  const SkipCount = pageNumber * pageSize;

  const params = {
    SkipCount,
    MaxResultCount: pageSize,
    FilterText: filter || undefined,

    DealerId: dealerId,
    PickUpAddress: pickUpAddress,
    DropOffAddress: dropOffAddress,
    Status: status,
    Distance: distance,
    DealerName: dealerName,
    DealerPrice: dealerPrice,
    PickupETA: pickupETA,
    DropOffETA: dropOffETA,
    PostedAt: postedAt,
    PickedUpAt: pickedUpAt,
    VehicleCount: vehicleCount,
    CreationTime: creationTime,
    CreatorId: creatorId,

    Sorting,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments`, {
    method: "GET",
    params,
  });

  return result?.data;
}

export async function getShareShipment(params) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/share/${params}`, {
    method: "GET",
  });

  return result?.data;
}

export async function shareShipment(params) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/share`, {
    method: "POST",
    data: params,
  });

  if ("error" in result) {
    throw new Error(result.error);
  }

  return result;
}

export async function getAllAdminShipments({
  page = 1,
  pageSize = 10,
  filter = "",

  dealerId,
  transporterId,
  pickUpAddressId,
  dropOffAddressId,
  pickupETAMin,
  pickupETAMax,
  dropoffETAMin,
  dropoffETAMax,
  status,

  distanceMin,
  distanceMax,

  sorting,
  companyGroupId,
  shipmentStatus,
  dropOffStateId,
  pickupStateId,
  pickupCity,
  dropOffCity,

  createdAt,
  postedAt,
  pickedUpAt,
  droppedOffAt,
  pickedUpETA,
  droppedOffETA,

  isHardRoute,
  isProblemRide,
  isAutosledExpedited,
  requiresTWIC,
  requiresEModal,
  requiresLotExit,
  isEnclosed,
  isInoperational,
  isDirectBrokerage,
  isVisible,
  managerId,
  securePassValue,
}) {
  const pageNumber = page - 1;

  let Sorting;

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");

    const sortByDict = {
      dealerName: "dealer.name",
      pickedUpAt: "load.pickedUpAt",
      droppedOffAt: "load.droppedOffAt",
      pickupETA: "load.pickupETA",
      dropOffETA: "load.dropOffETA",
    };

    const sortByValue = sortByDict[sortBy] || sortBy;
    Sorting = `${sortByValue} ${sortOrder}`;
  }

  const SkipCount = pageNumber * pageSize;

  const dateCriteria = buildDateCriteria(
    {
      creationTime: createdAt,
      postedAt,
      "load.pickedUpAt": pickedUpAt,
      "load.droppedOffAt": droppedOffAt,
      "load.pickupETA": pickedUpETA,
      "load.dropoffETA": droppedOffETA,
    },
    "DateCriteria",
  );

  const dateParams = dateCriteria !== "" ? `?${dateCriteria}` : "";

  const getDrivabilityType = () => {
    if (isInoperational) return Drivability.Inoperational;
    if (isInoperational === false) return Drivability.RideAndDrive;
    return undefined;
  };

  const getTrailerType = () => {
    if (isEnclosed) return TrailerType.Enclosed;
    if (isEnclosed === false) return TrailerType.Open;
    return undefined;
  };

  const drivabilityType = getDrivabilityType();
  const isEnclosedType = getTrailerType();

  const params = {
    SkipCount,
    MaxResultCount: pageSize,
    FilterText: filter || undefined,

    DealerId: dealerId,
    TransporterId: transporterId,
    PickupAddressId: pickUpAddressId,
    DropOffAddressId: dropOffAddressId,

    PickupETAMin: pickupETAMin,
    PickupETAMax: pickupETAMax,
    DropOffETAMin: dropoffETAMin,
    DropOffETAMa: dropoffETAMax,
    ShipmentStatus: status,

    DistanceMin: distanceMin,
    DistanceMax: distanceMax,

    Sorting,
    CompanyGroupId: companyGroupId,
    Status: shipmentStatus,
    DropoffStateId: dropOffStateId,
    PickupStateId: pickupStateId,
    PickupCity: pickupCity,
    DropOffCity: dropOffCity,

    IsHardRoute: isHardRoute,
    HasProblemRide: isProblemRide,
    AutosledExpedited: isAutosledExpedited,
    IsDirectBrokerage: isDirectBrokerage,
    IsVisible: isVisible,

    RequiresTWIC: requiresTWIC,
    RequiresEModal: requiresEModal,
    RequiresLotExit: requiresLotExit,

    DrivabilityType: drivabilityType,
    TrailerType: isEnclosedType,

    ManagerId: managerId,
    SecurePass: securePassValue,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/admin${dateParams}`, {
    method: "GET",
    params,
  });

  return result?.data;
}

export async function deleteShipment(shipmentId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/${shipmentId}`, {
    method: "DELETE",
  });

  return result || { error: true };
}

export async function dealerCancelShipment(shipmentId, reason, otherReason) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/cancel/${shipmentId}`, {
    method: "DELETE",
    params: {
      reason,
      otherReason,
    },
  });

  if (result?.response && result?.response?.status === 204) {
    return result;
  }

  return { error: true };
}

export async function createNewDealerExchange(shipmentId, dealerExchangeType, isExchangeRequired) {
  const data = {
    shipmentId,
    dealerExchangeType,
    isExchangeRequired,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/exchange`, {
    method: "POST",
    data,
  });

  return result.data;
}

export async function editShipment(shipmentId, payload) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/${shipmentId}`, {
    method: "PUT",
    data: payload,
  });

  if (result?.response?.status === 200 && result?.data) {
    return result.data;
  }

  return { error: true };
}

export async function editAdminShipment(shipmentId, payload) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/admin/${shipmentId}`, {
    method: "PUT",
    data: payload,
  });

  if (result?.response?.status === 200 && result?.data) {
    return result.data;
  }

  return { error: true };
}

export async function adminPostShipment(shipmentId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/admin/post/${shipmentId}`, {
    method: "PATCH",
  });

  return result.data;
}

export async function createShipmentFromTenderFile(data) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/complete`, {
    method: "POST",
    data,
  });

  if ("error" in result) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function bulkUpdateBayLocations(file) {
  const formData = new FormData();
  formData.append("file", file);

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/nissan-service/vehicles/bay-location`, {
    method: "PUT",
    data: formData,
  });

  if ("error" in result) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function getTransporterAvailableLoads({ page = 1, pageSize = 10, filter = "", sorting }) {
  const pageNumber = page - 1;

  let Sorting;

  const customSorting = {
    dealerName: "Dealer.Name",
    pickupDropoffLocation: "PickupAddress.SimplifyAddress",
  };

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");
    Sorting = `${customSorting[sortBy] || sortBy} ${sortOrder}`;
  }

  const SkipCount = pageNumber * pageSize;

  const params = {
    SkipCount,
    MaxResultCount: pageSize,
    FilterText: filter || undefined,
    Sorting,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/available`, {
    method: "GET",
    params,
  });

  return result;
}

export async function getAvailableTransporterShipment(shipmentId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/available/${shipmentId}`, {
    method: "GET",
  });

  return result.data;
}

export async function acceptManyShipments(data) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/loads/accept-many`, {
    method: "POST",
    data,
  });

  if (result?.error) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function acceptShipment(data) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/loads/accept`, {
    method: "POST",
    data,
  });

  if (result?.error) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function postAndAcceptAdminShipment(data) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/loads/post-accept-admin`, {
    method: "POST",
    data,
  });

  if (result?.error) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function assignAdminShipment(data) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/loads/accept-admin`, {
    method: "POST",
    data,
  });

  if (result?.error) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function getShipmentByVIN({ page = 1, pageSize = 10, filter = "", sorting }) {
  const pageNumber = page - 1;

  let Sorting;

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");
    Sorting = `${sortBy} ${sortOrder}`;
  }

  const SkipCount = pageNumber * pageSize;

  const params = {
    SkipCount,
    MaxResultCount: pageSize,
    VIN: filter || undefined,
    Sorting,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/vehicles/vin`, {
    method: "GET",
    params,
  });

  if (result.error) {
    throw new Error(result.error);
  }

  return result?.data;
}

export async function getAdminPricingHistory({
  distance,
  dropOffZipCode,
  dropOffCity,
  dropOffState,
  pickupZipCode,
  pickupCity,
  pickupState,
  type,
  vehicleType,
  shipmentStatus,
  fromDate,
  toDate,
  isHardRoute,
  isProblemRide,
  isAutosledExpedited,
  sorting,
}) {
  let Sorting;

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");
    Sorting = `${sortBy} ${sortOrder}`;
  }

  const isFilterDate = !!fromDate && !!toDate;

  const params = {
    PickupCity: pickupCity,
    PickupState: pickupState,
    DropOffCity: dropOffCity,
    DropOffState: dropOffState,
    PickupZipCode: pickupZipCode,
    DropOffZipCode: dropOffZipCode,
    Distance: distance,
    FromDate: isFilterDate ? moment(fromDate).format("MM/DD/YYYY") : undefined,
    ToDate: isFilterDate ? moment(toDate).format("MM/DD/YYYY") : undefined,
    IsHardRoute: isHardRoute ? Boolean(isHardRoute) : undefined,
    IsProblemRide: isProblemRide ? Boolean(isProblemRide) : undefined,
    IsExpedited: isAutosledExpedited ? Boolean(isAutosledExpedited) : undefined,
    Status: shipmentStatus,
    VehicleType: vehicleType,
    IsFilterDate: isFilterDate,
    Type: type,
    Sorting,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/pricing-history`, {
    method: "GET",
    params: cleanObjectFromUndefined(params),
  });

  if (result?.error) {
    throw new Error(result?.error);
  }

  return result?.data;
}

export async function getTransporterReport({
  page = 1,
  pageSize = 10,
  filter = "",
  sorting = undefined,
  startDate = "",
  endDate = "",
}) {
  const pageNumber = page - 1;

  let Sorting;

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");
    Sorting = `${sortBy} ${sortOrder}`;
  }

  const SkipCount = pageNumber * pageSize;

  const params = {
    SkipCount,
    MaxResultCount: pageSize,
    Search: filter || undefined,
    StartDate: startDate || undefined,
    EndDate: endDate || undefined,
    Sorting,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/vehicles/transporter-reports`, {
    method: "GET",
    params: cleanObjectFromUndefined(params),
  });

  if ("error" in result) {
    throw new Error(result.error);
  }

  return result;
}

export async function getDealerReport({
  page = 1,
  pageSize = 10,
  filter = "",
  sorting = undefined,
  startDate = "",
  endDate = "",
}) {
  const pageNumber = page - 1;

  let Sorting;

  if (typeof sorting === "string" && sorting.indexOf(":") !== -1) {
    const [sortBy, sortOrder] = sorting.split(":");
    Sorting = `${sortBy} ${sortOrder}`;
  }

  const SkipCount = pageNumber * pageSize;

  const params = {
    SkipCount,
    MaxResultCount: pageSize,
    Search: filter || undefined,
    StartDate: startDate || undefined,
    EndDate: endDate || undefined,
    Sorting,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/vehicles/dealer-reports`, {
    method: "GET",
    params: cleanObjectFromUndefined(params),
  });

  if ("error" in result) {
    throw new Error(result.error);
  }

  return result;
}

export async function getAvailablePickupDates(shipmentId, startDate) {
  const params = {
    pickupDate: startDate,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/pickup-dates/${shipmentId}`, {
    method: "GET",
    params: cleanObjectFromUndefined(params),
  });

  if ("error" in result) {
    throw new Error(result.error);
  }
  return result?.data;
}

export async function getAvailableDropoffDates(shipmentId, pickupDate) {
  const params = {
    pickupDate: pickupDate?.format("MM/DD/YYYY"),
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/dropoff-dates/${shipmentId}`, {
    method: "GET",
    params: cleanObjectFromUndefined(params),
  });

  if ("error" in result) {
    throw new Error(result.error);
  }

  return result?.data;
}

export async function getAvailableShipmentMarkers() {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/all-available`, {
    method: "GET",
  });

  if ("error" in result) {
    throw new Error(result.error);
  }

  return result?.data;
}

export async function dealerRequestCancel(shipmentId, reason, otherReason) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/request-cancel/${shipmentId}`,
    {
      method: "DELETE",
      data: {
        reason,
        otherReason,
      },
    },
  );

  if ("error" in result) {
    throw new Error(result?.error);
  }

  return result;
}

export async function cancelAdminShipment(shipmentId, data) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/core-service/shipments/admin/cancel/${shipmentId}`, {
    method: "DELETE",
    data,
  });

  if ("error" in result) {
    throw new Error(result?.error);
  }

  return result;
}
